import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, X } from "lucide-react";
import Reveal from "./Reveal";
import { useLang } from "../i18n";
import { track, API } from "@/lib/track";
import ShareAssessment from "@/components/ShareAssessment";

const disciplines = [
  { n: "01", nk: "disc.d1n", dk: "disc.d1d", xk: "disc.d1x", name: "Space-Time & Matter", desc: "Life unfolds inside a physical fabric. We study how matter organizes, persists, and repairs across time.", explainer: "Medicine measures aging in years; physics asks what time itself is. By studying how matter organizes and persists across space-time, we look for the deepest levers that let living systems resist decay.", span: "md:col-span-2", img: null },
  { n: "02", nk: "disc.d2n", dk: "disc.d2d", xk: "disc.d2x", name: "Particle Physics", desc: "At the smallest scales lie the rules everything else obeys.", explainer: "Every drug, cell and thought is built from particles obeying precise rules. Understanding those rules at the smallest scales lets medicine design interventions with atomic accuracy instead of approximation.", span: "", img: null },
  { n: "03", nk: "disc.d3n", dk: "disc.d3d", xk: "disc.d3x", name: "Quantum Mechanics", desc: "Coherence, decoherence, and the quantum basis of biological machinery.", explainer: "Life already runs on quantum machinery: enzymes, photosynthesis and possibly cognition. Where medicine meets quantum physics, we explore coherence, tunneling and sensing as new tools to read and repair biology.", span: "md:row-span-2", img: "https://images.pexels.com/photos/38032287/pexels-photo-38032287.png?auto=compress&cs=tinysrgb&w=900" },
  { n: "04", nk: "disc.d4n", dk: "disc.d4d", xk: "disc.d4x", name: "AI & Computing", desc: "Simulation and machine intelligence compress decades of biological trial into days of compute.", explainer: "AI compresses decades of medical trial and error into days of simulation. Paired with physical models of the body, it lets us test molecules and therapies in silico before they ever reach a patient.", span: "md:col-span-2", img: null },
  { n: "05", nk: "disc.d5n", dk: "disc.d5d", xk: "disc.d5x", name: "Biology", desc: "Cellular repair, senescence, and regeneration, the direct levers of lifespan.", explainer: "Biology gives medicine its targets: cellular repair, senescence and regeneration. Combined with physics-grade measurement, aging turns from a mystery into an engineering specification.", span: "md:col-span-2", img: "https://images.pexels.com/photos/35967917/pexels-photo-35967917.png?auto=compress&cs=tinysrgb&w=1260" },
  { n: "06", nk: "disc.d6n", dk: "disc.d6d", xk: "disc.d6x", name: "Neurology", desc: "The brain is the irreplaceable core. Protecting and extending it is non-negotiable.", explainer: "The brain is where medicine cannot yet replace what fails. Neurology converges with physics and computing to protect, interface with and one day extend the irreplaceable core of who we are.", span: "", img: null },
  { n: "07", nk: "disc.d7n", dk: "disc.d7d", xk: "disc.d7x", name: "Psychology & Cognitive Science", desc: "A longer life must remain a life worth living, mind, memory, and meaning included.", explainer: "A longer life only matters if the mind thrives. Cognitive science and medicine together map how memory, motivation and meaning change with age, so added years stay worth living.", span: "md:col-span-2", img: null },
  { n: "08", nk: "disc.d8n", dk: "disc.d8d", xk: "disc.d8x", name: "Health Sciences", desc: "Public health, nutrition and prevention, the population-scale levers that have already added decades to human life.", explainer: "Public health added decades to human life without a single pill: sanitation, nutrition, prevention. Measured with physical precision, these population levers become programmable infrastructure for longevity.", span: "", img: null },
  { n: "09", nk: "disc.d9n", dk: "disc.d9d", xk: "disc.d9x", name: "Medical Science", desc: "Clinical medicine turns longevity research into practice: diagnostics, therapies and care that extend healthy years.", explainer: "Medicine turns every discovery into practice. Converging with physics and engineering, diagnostics, imaging and therapies gain the resolution to catch aging early and reverse it precisely.", span: "md:col-span-2", img: null },
];

/* Discipline Quiz Match: 3 questions, weighted scoring, result opens the
   matching discipline's deep-dive modal. English-only per new-feature rule. */
const QUIZ = [
  {
    q: "What pulls you most?",
    opts: [
      ["The nature of time and reality itself", { "01": 3, "02": 2, "03": 1 }],
      ["Cracking problems with data and machines", { "04": 3, "03": 1, "06": 1 }],
      ["How the body and brain work", { "05": 2, "06": 2, "09": 1 }],
      ["Helping people live better at scale", { "08": 3, "09": 2, "07": 1 }],
    ],
  },
  {
    q: "Pick a work style:",
    opts: [
      ["Theory, equations, thought experiments", { "01": 2, "02": 2, "03": 2 }],
      ["Building tools, models and simulations", { "04": 3, "03": 1, "09": 1 }],
      ["Lab work, experiments, clinical questions", { "09": 2, "05": 2, "06": 2 }],
      ["Fieldwork, communities, human behavior", { "08": 2, "07": 3 }],
    ],
  },
  {
    q: "Which longevity outcome excites you most?",
    opts: [
      ["Proving physics allows a far longer life", { "01": 2, "03": 2, "02": 2 }],
      ["Discoveries that reach patients faster", { "09": 3, "04": 1, "05": 1 }],
      ["Protecting the brain and the mind", { "06": 3, "07": 2 }],
      ["Healthier populations, prevention first", { "08": 3, "09": 1, "05": 1 }],
    ],
  },
];

const Disciplines = () => {
  const { t } = useLang();
  const [open, setOpen] = useState(null);
  const [quizOpen, setQuizOpen] = useState(false);
  const [quizStep, setQuizStep] = useState(0);
  const [scores, setScores] = useState({});
  const [quizDone, setQuizDone] = useState(false);

  const quizMatch = quizDone
    ? disciplines.reduce((best, d) => ((scores[d.n] || 0) > (scores[best.n] || 0) ? d : best), disciplines[0])
    : null;

  const quizStart = () => { setQuizStep(0); setScores({}); setQuizDone(false); setQuizOpen(true); track("discipline_quiz_started"); };
  const quizAnswer = (weights) => {
    const next = { ...scores };
    Object.entries(weights).forEach(([k, v]) => { next[k] = (next[k] || 0) + v; });
    setScores(next);
    if (quizStep < QUIZ.length - 1) setQuizStep(quizStep + 1);
    else {
      setQuizDone(true);
      const win = disciplines.reduce((b, d) => ((next[d.n] || 0) > (next[b.n] || 0) ? d : b), disciplines[0]);
      track("discipline_quiz_completed", { match: win.name });
    }
  };

  /* ESC closes whichever modal is open */
  useEffect(() => {
    if (!open && !quizOpen) return undefined;
    const onKey = (e) => { if (e.key === "Escape") { setOpen(null); setQuizOpen(false); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, quizOpen]);

  return (
    <section id="disciplines" data-testid="disciplines-section" className="relative bg-surface/50 py-28 md:py-40">
      <div className="mx-auto max-w-6xl px-6">
        <Reveal testId="disciplines-heading" className="text-center">
          <p className="text-gradient mb-6 font-mono text-base uppercase tracking-[0.3em]">
            {t("disc.eyebrow", "The Stack")}
          </p>
          <h2 className="mx-auto max-w-3xl font-display text-[2rem] font-semibold leading-[1.08] tracking-tight sm:text-[2.75rem]">
            {t("disc.h2a", "Nine disciplines.")} <span className="text-gradient">{t("disc.h2b", "One variable.")}</span>
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-base font-light leading-relaxed text-fog">
            {t("disc.sub", "Longevity is not a biology problem alone. It is a full-stack problem, from particles to consciousness.")}
          </p>
          <button
            type="button"
            onClick={quizStart}
            data-testid="disc-quiz-open"
            className="mx-auto mt-8 flex min-h-[48px] items-center gap-2 rounded-full border border-cyan-300/30 bg-cyan-400/5 px-6 text-sm font-semibold text-cyan-200 transition-colors duration-300 hover:border-cyan-300/60 hover:bg-cyan-400/10"
          >
            Find your discipline in 3 questions <ArrowUpRight className="h-4 w-4" />
          </button>
        </Reveal>

        <div className="mt-16 grid gap-6 md:grid-cols-3">
          {disciplines.map((d, i) => (
            <Reveal key={d.n} delay={(i % 3) * 0.08} testId={`discipline-card-${d.n}`} className={d.span}>
              <article
                role="button"
                tabIndex={0}
                onClick={() => setOpen(d)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen(d); } }}
                data-testid={`discipline-open-${d.n}`}
                className="group relative flex h-full min-h-[260px] w-full cursor-pointer flex-col justify-end overflow-hidden rounded-2xl border border-white/10 bg-surface p-8 text-left transition-colors duration-500 hover:border-cyan-300/30 focus:outline-none focus-visible:border-cyan-300/60"
              >
                {d.img && (
                  <div className="absolute inset-0">
                    <img
                      src={d.img}
                      alt={d.name}
                      loading="lazy"
                      className="h-full w-full object-cover brightness-[0.6] contrast-125 grayscale transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                    <div className="card-veil absolute inset-0 bg-gradient-to-t from-surface via-surface/50 to-transparent" />
                  </div>
                )}
                <div className="relative">
                  <span className="text-gradient font-mono text-base tracking-[0.2em]">
                    [{d.n}]
                  </span>
                  <h3 className="mt-4 font-display text-xl font-semibold tracking-tight md:text-2xl">
                    {t(d.nk, d.name)}
                  </h3>
                  <p className="mt-3 max-w-md text-base font-light leading-relaxed text-fog">
                    {t(d.dk, d.desc)}
                  </p>
                  <span
                    data-testid={`discipline-dive-${d.n}`}
                    className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-cyan-300/70 transition-colors duration-300 group-hover:text-cyan-300"
                  >
                    {t("disc.more", "How it converges")}
                    <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>

      {/* deep-dive explainer modal */}
      <AnimatePresence>
        {open && (
          <motion.div
            key="disc-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={() => setOpen(null)}
            data-testid="discipline-modal"
            className="fixed inset-0 z-[80] flex items-center justify-center bg-void/80 p-5 backdrop-blur-sm"
          >
            <motion.div
              role="dialog"
              aria-modal="true"
              aria-label={t(open.nk, open.name)}
              initial={{ opacity: 0, y: 24, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 16, scale: 0.98 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="relative max-h-[90dvh] w-full max-w-lg overflow-y-auto rounded-3xl border border-white/10 bg-surface p-7 shadow-2xl sm:p-9"
            >
              <button
                type="button"
                aria-label="Close"
                onClick={() => setOpen(null)}
                data-testid="discipline-modal-close"
                className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full border border-white/10 text-fog transition-colors hover:text-white"
              >
                <X className="h-4 w-4" />
              </button>
              <span className="text-gradient font-mono text-sm tracking-[0.2em]">[{open.n}]</span>
              <h3 className="mt-3 pr-10 font-display text-2xl font-semibold tracking-tight" data-testid="discipline-modal-title">
                {t(open.nk, open.name)}
              </h3>
              <p className="mt-3 text-base font-light leading-relaxed text-fog">{t(open.dk, open.desc)}</p>
              <p className="mt-4 border-t border-white/10 pt-4 text-base font-light leading-relaxed text-bone" data-testid="discipline-modal-body">
                {t(open.xk, open.explainer)}
              </p>
              <p className="mt-5 font-mono text-[11px] uppercase tracking-[0.2em] text-white/35" data-testid="discipline-modal-tag">
                {t("disc.converge", "Medicine × Physics × Longevity")}
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* quiz modal */}
      <AnimatePresence>
        {quizOpen && (
          <motion.div
            key="disc-quiz"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={() => setQuizOpen(false)}
            data-testid="disc-quiz-modal"
            className="fixed inset-0 z-[80] flex items-center justify-center bg-void/80 p-5 backdrop-blur-sm"
          >
            <motion.div
              role="dialog"
              aria-modal="true"
              aria-label="Discipline quiz"
              initial={{ opacity: 0, y: 24, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 16, scale: 0.98 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="relative max-h-[90dvh] w-full max-w-lg overflow-y-auto rounded-3xl border border-white/10 bg-surface p-7 shadow-2xl sm:p-9"
            >
              <button
                type="button"
                aria-label="Close"
                onClick={() => setQuizOpen(false)}
                data-testid="disc-quiz-close"
                className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full border border-white/10 text-fog transition-colors hover:text-white"
              >
                <X className="h-4 w-4" />
              </button>
              {!quizDone ? (
                <>
                  <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-cyan-300" data-testid="disc-quiz-step">
                    Question {quizStep + 1} of {QUIZ.length}
                  </p>
                  <div className="mt-3 flex gap-1.5" aria-hidden="true">
                    {QUIZ.map((_, i) => (
                      <span key={i} className={`h-1 flex-1 rounded-full ${i <= quizStep ? "bg-cyan-300" : "bg-white/10"}`} />
                    ))}
                  </div>
                  <h3 className="mt-5 font-display text-2xl font-semibold tracking-tight" data-testid="disc-quiz-question">
                    {QUIZ[quizStep].q}
                  </h3>
                  <div className="mt-5 grid gap-2.5">
                    {QUIZ[quizStep].opts.map(([label, weights], i) => (
                      <button
                        key={label}
                        type="button"
                        onClick={() => quizAnswer(weights)}
                        data-testid={`disc-quiz-opt-${i}`}
                        className="min-h-[52px] rounded-xl border border-white/10 bg-void px-4 py-3 text-left text-sm font-medium text-fog transition-colors duration-200 hover:border-cyan-300/40 hover:text-white"
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                <div data-testid="disc-quiz-result" className="text-center">
                  <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-cyan-300">Your closest match</p>
                  <span className="text-gradient mt-4 inline-block font-mono text-base tracking-[0.2em]">[{quizMatch.n}]</span>
                  <h3 className="mt-2 font-display text-2xl font-semibold tracking-tight" data-testid="disc-quiz-match-name">
                    {t(quizMatch.nk, quizMatch.name)}
                  </h3>
                  <p className="mt-3 text-sm font-light leading-relaxed text-fog">{t(quizMatch.dk, quizMatch.desc)}</p>
                  <div className="mt-6 flex flex-wrap justify-center gap-3">
                    <button
                      type="button"
                      onClick={() => { setQuizOpen(false); setOpen(quizMatch); }}
                      data-testid="disc-quiz-dive"
                      className="bg-gradient-green flex min-h-[48px] items-center gap-2 rounded-full px-6 text-sm font-bold text-black"
                    >
                      Read the deep dive <ArrowUpRight className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={quizStart}
                      data-testid="disc-quiz-retake"
                      className="flex min-h-[48px] items-center rounded-full border border-white/15 px-6 text-sm font-semibold text-fog hover:text-white"
                    >
                      Retake
                    </button>
                  </div>
                  <div className="mt-6 border-t border-white/10 pt-5">
                    <img
                      src={`${API}/quiz/card/${quizMatch.n}.png`}
                      alt={`My discipline match card: ${quizMatch.name}`}
                      data-testid="disc-quiz-card-preview"
                      className="mx-auto w-full max-w-[280px] rounded-xl border border-white/10"
                      loading="lazy"
                    />
                    <div className="mt-4">
                      <ShareAssessment
                        variant="compact"
                        testId="disc-quiz-share"
                        cardUrl={`${API}/quiz/card/${quizMatch.n}.png`}
                        downloadUrl={`${API}/quiz/card/${quizMatch.n}.png?download=1`}
                        shareText={`My discipline match on The Lifespan Project quiz: *${quizMatch.name}* [${quizMatch.n}].\nWhich of the 9 longevity disciplines fits you? Take the free 3-question quiz:`}
                        shareUrl={`https://thelifespanproject.com/api/share/quiz/${quizMatch.n}`}
                      />
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Disciplines;
